<template>
  <nuttrema-layout-content>

    <template slot="header">
      <persons-standard-header
        :meta="meta"
        :PersonDetails="PersonDetails">
        <template slot="edit">
          <div class="content-header-item">
            <permissions-control-wrapper
              :required-general="$PERMISSIONS_GENERAL.EDIT"
              :permissions="_get(PersonDetails, 'meta.permissions')">
              <edit-lock-button slot="allowed" :edit.sync="edit"></edit-lock-button>
              <edit-lock-button slot="not-allowed" :lock="true"></edit-lock-button>
            </permissions-control-wrapper>
          </div>
        </template>
      </persons-standard-header>
    </template>

    <template v-if="!isEvolutionConfigured">
      <nuttrema-empty-result
        slot="central"
        class="is-white is-with-border-radius with-logo"
        icon="fal fa-exclamation-triangle">
        <div slot="text">
          <h1>{{ $t('label.attention') }}</h1>
          <h4>{{ $t('label.attention_text') }}</h4>
        </div>
        <div slot="button">
          <router-link
            :to="{ name: 'participant-evolution-settings-general' }">
            <el-button type="text">
              {{ $t('label.evolution_settings') }} <i class="el-icon-arrow-right" aria-hidden="true"></i>
            </el-button>
          </router-link>
        </div>
      </nuttrema-empty-result>
    </template>
    <!-- end ~ isEvolutionConfigured false -->

    <template v-else>
      <measurements-select-time-period
         slot="sub-header"
        :activeView.sync="options.activeView"
        :period="options.period"
        :dateFrom="options.dateFrom"
        :dateTo="options.dateTo"
        @changeTimePeriod="onChangeTimePeriod">
      </measurements-select-time-period>

      <template slot="central">

        <nuttrema-loader v-if="PersonMeasurements.fetching"></nuttrema-loader>

        <nuttrema-empty-result
           v-else-if="_get(PersonMeasurements, 'error')"
           class="with-logo"
           predefined-set="forbiden"
          :title="_get(PersonMeasurements, 'error.error')"
          :sub-title="_get(PersonMeasurements, 'error.message')">
        </nuttrema-empty-result>

        <nuttrema-empty-result
          v-else-if="_isEmpty(_get(PersonMeasurements, 'data.list', []))"
          class="with-logo"
          predefined-set="landscape">
        </nuttrema-empty-result>

        <template v-else>
          <keep-alive>
            <component
              v-bind:is="options.activeView"
             :id="id"
             :edit="edit"
             :period="options.period"
             :dateFrom="options.dateFrom"
             :dateTo="options.dateTo"
             :meta="meta">
            </component>
          </keep-alive>
        </template>

      </template>
      <!-- end ~ slot - central -->

    </template>
    <!-- end ~ isEvolutionConfigured true -->

    <router-view slot="central"></router-view>
  </nuttrema-layout-content>
</template>

<script>
/* eslint-disable new-cap */
/* eslint-disable object-shorthand */
import Vue from 'vue';

import PageProps from 'components/mixins/page-props';

import moment from 'moment';
import { isEmpty, assign, get } from 'lodash';

import * as calendar from 'utils/calendar';
import { LocalStoreOptions } from 'services';

import * as KEYS from 'constants/keys';
import { PERSON_MEASUREMENTS_TOTAL } from 'constants/values';

import NuttremaLayoutContent from 'components/ui/layout/nuttrema-layout-content';

import PersonsStandardHeader from 'components/pages/professional/persons/participants/shared/blocks/cmpt-persons-standard-header';

import MeasurementsSelectTimePeriod from './cmpt-measurements-select-time-period';

import PageEvolutionMeasurementsList from './page-evolution-measurements-list';
import PageEvolutionMeasurementsGraph from './page-evolution-measurements-graph';

const defaultPeriod = calendar.timePeriod.thisWeek;

export default {
  name: 'page-evolution-dashboard',
  mixins: [
    PageProps,
  ],
  props: {
    id: {
      required: true,
      default: null,
    },
    type: {
      required: true,
      default: null,
    },
  },
  components: {
    NuttremaLayoutContent,
    PersonsStandardHeader,
    MeasurementsSelectTimePeriod,
    PageEvolutionMeasurementsList,
    PageEvolutionMeasurementsGraph,
  },
  data() {
    return {
      selectedPeriod: null,
      edit: false,
      options: {
        activeView: 'page-evolution-measurements-list',
        period: defaultPeriod,
        dateFrom: null,
        dateTo: null,
      },
    };
  },
  methods: {
    onChangeTimePeriod(params) {
      // console.log('onChangeTimePeriod', params, isEmpty(params));
      if (!isEmpty(params)) {
        Vue.set(this, 'options', assign({}, this.options, params));
        this.selectedPeriod = calendar.getPeriodFromOptions(this.options);
        this.fetchPersonMeasurements();
      }
    },
    fetchPersonMeasurements() {
      this.$store.dispatch('GetPersonMeasurements',
        {
          id: this.id,
          type: this.type,
          query: {
            per_page: PERSON_MEASUREMENTS_TOTAL,
            date_from: moment(this.selectedPeriod[0]).format('YYYY-MM-DD'),
            date_to: moment(this.selectedPeriod[1]).format('YYYY-MM-DD'),
          },
        },
      );
    },
    showMaxResponseExceeded() {
      this.$notify({
        duration: 0,
        type: 'warning',
        title: this.$t('label.warning'),
        message: this.$t('message.error_response_exceeds_maximum_measurement', { value: PERSON_MEASUREMENTS_TOTAL }),
      });
    },
  },
  computed: {
    PersonDetails() {
      return this.$store.getters.PersonDetails(this.id);
    },
    PersonMeasurements() {
      return this.$store.getters.PersonMeasurements(this.id);
    },
    isEvolutionConfigured() {
      return get(this.PersonDetails, 'data.evolution_configured', false);
    },
  },
  watch: {
    id() {
      this.fetchPersonMeasurements();
    },
    options: {
      handler: (newValue) => {
        LocalStoreOptions.set(KEYS.PAGE_EVOLUTION_DASHBOARD, newValue);
      },
      deep: true,
    },
    'PersonMeasurements.meta.pagination': {
      handler: function (newValue) {
        const totalPages = get(newValue, 'total_pages', 1) || 1;
        if (totalPages > 1) this.showMaxResponseExceeded();
      },
      deep: true,
    },
  },
  created() {
    LocalStoreOptions.get(KEYS.PAGE_EVOLUTION_DASHBOARD, this.options);
    this.selectedPeriod = calendar.getPeriod(this.options.period, this.options.dateFrom, this.options.dateTo);
    this.fetchPersonMeasurements();
    // this.$store.dispatch('GetParticipantSettingsTrackedMeasurements', { id: this.id });
  },
  beforeDestroy() {
    this.$store.dispatch('ClearPersonMeasurementsStore', { id: this.id });
    // this.$store.dispatch('ClearParticipantSettingsTrackedMeasurements');
  },
};
</script>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/pages/professional/persons/participants/participant-evolution/dashboard/page-evolution-dashboard.vue